import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  AppBar,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Toolbar,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import { useNewsletterEditor } from "../context/NewsletterEditorContext";
import ConfirmDialog from "../dialogs/ConfirmDialog";
import EditorDialog from "../dialogs/EditorDialog";
import {
  deleteNewsletterEmail,
  listNewsletterEmails,
  loadNewsletterDocument,
  saveNewsletterDocument,
} from "../../../../services/newsletterStorageService";
import { formatStorageDisplayName } from "../utils/storageDisplayUtils";
import { compileDocumentToHtml } from "../services/htmlCompiler";
import { createEmptyDocument } from "../model/documentModel";
import { EDITOR_ACCENT } from "../editorUiConstants";
import { resolveNewsletterDisplayName, resolveNewsletterSaveName } from "../utils/documentNameUtils";
import {
  contentSectionMenubarSx,
  contentSectionMenubarToolbarSx,
} from "../../content/contentEditorStyles";

const EditorToolbar = ({ compileErrors, openListSignal }) => {
  const {
    document,
    dirty,
    savedObjectKey,
    workspaceActive,
    activateWorkspace,
    setDirty,
    setSavedObjectKey,
    resetDocument,
    undo,
    redo,
    replaceMeta,
    loadDocument,
    showEditorMessage,
    setSelection,
  } = useNewsletterEditor();

  const [openDialog, setOpenDialog] = useState(false);
  const [emailList, setEmailList] = useState([]);
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [saveAsName, setSaveAsName] = useState(document.name);
  const [loading, setLoading] = useState(false);
  const [confirmNewOpen, setConfirmNewOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const displayName = useMemo(() => resolveNewsletterDisplayName(document.name), [document.name]);

  const handleSave = useCallback(
    async (objectKeyOverride, nameOverride) => {
      setLoading(true);
      try {
        const resolvedName = resolveNewsletterSaveName(nameOverride ?? document.name);
        const payload = { ...document, name: resolvedName };
        const key = objectKeyOverride === null ? undefined : objectKeyOverride ?? savedObjectKey;
        const result = await saveNewsletterDocument(payload, key);
        if (result?.objectKey) {
          setSavedObjectKey(result.objectKey);
        }
        setDirty(false);
        if (nameOverride || resolvedName !== document.name) {
          replaceMeta({ name: resolvedName });
        }
        return result;
      } catch (err) {
        showEditorMessage({
          title: "Salvataggio non riuscito",
          message: err.message,
        });
        return null;
      } finally {
        setLoading(false);
      }
    },
    [document, replaceMeta, savedObjectKey, setDirty, setSavedObjectKey, showEditorMessage]
  );

  const handleOpenList = useCallback(async () => {
    setLoading(true);
    try {
      const items = await listNewsletterEmails();
      setEmailList(items);
      setOpenDialog(true);
    } catch (err) {
      showEditorMessage({
        title: "Caricamento email salvate non riuscito",
        message: err.message,
      });
    } finally {
      setLoading(false);
    }
  }, [showEditorMessage]);

  useEffect(() => {
    if (openListSignal > 0) {
      handleOpenList();
    }
  }, [openListSignal, handleOpenList]);

  const handleOpenDocument = async (objectKey) => {
    setLoading(true);
    try {
      const loaded = await loadNewsletterDocument(objectKey);
      loadDocument(loaded, objectKey);
      setOpenDialog(false);
    } catch (err) {
      showEditorMessage({
        title: "Caricamento bozza non riuscito",
        message: err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEmail = async (objectKey) => {
    setLoading(true);
    try {
      await deleteNewsletterEmail(objectKey);
      setEmailList((current) => current.filter((item) => item.objectKey !== objectKey));
      if (savedObjectKey === objectKey) {
        setSavedObjectKey(null);
      }
      setDeleteConfirm(null);
    } catch (err) {
      showEditorMessage({
        title: "Eliminazione email non riuscita",
        message: err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    resetDocument(createEmptyDocument());
    activateWorkspace();
  };

  const handleExportHtml = () => {
    const { html, errors } = compileDocumentToHtml(document);
    if (errors?.length > 0 || !html?.trim()) {
      showEditorMessage({
        title: "Esportazione non disponibile",
        message: "Correggi gli errori di compilazione HTML prima di esportare.",
        detail: errors?.[0],
      });
      return;
    }
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = globalThis.document.createElement("a");
    anchor.href = url;
    anchor.download = `${resolveNewsletterSaveName(document.name)}.html`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const handleNew = () => {
    if (dirty) {
      setConfirmNewOpen(true);
      return;
    }
    handleCreateNew();
  };

  useEffect(() => {
    setSaveAsName(document.name);
  }, [document.name]);

  return (
    <>
      <AppBar position="static" color="default" elevation={0} sx={contentSectionMenubarSx}>
        <Toolbar sx={contentSectionMenubarToolbarSx}>
          <Button startIcon={<AddIcon />} onClick={handleNew} size="small">
            Nuovo
          </Button>
          <Button startIcon={<FolderOpenIcon />} onClick={handleOpenList} size="small" disabled={loading}>
            Apri
          </Button>
          <Button startIcon={<SaveIcon />} onClick={() => handleSave()} size="small" disabled={loading || !workspaceActive}>
            Salva
          </Button>
          <Button startIcon={<SaveIcon />} onClick={() => setSaveAsOpen(true)} size="small" disabled={loading || !workspaceActive}>
            Salva con nome
          </Button>
          <Button startIcon={<FileDownloadOutlinedIcon />} onClick={handleExportHtml} size="small" disabled={!workspaceActive}>
            Esporta HTML
          </Button>
          <Button startIcon={<UndoIcon />} onClick={undo} size="small" disabled={!workspaceActive}>
            Undo
          </Button>
          <Button startIcon={<RedoIcon />} onClick={redo} size="small" disabled={!workspaceActive}>
            Redo
          </Button>
          {compileErrors?.length ? (
            <Button
              size="small"
              color="error"
              onClick={() =>
                showEditorMessage({
                  title: "Errori compilazione HTML",
                  message: "La newsletter contiene problemi nella generazione HTML.",
                  detail: compileErrors.join(" "),
                })
              }
            >
              Errori HTML ({compileErrors.length})
            </Button>
          ) : null}
          <Box sx={{ flexGrow: 1 }} />
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ ml: 1 }}>
            <Chip
              label={displayName}
              size="small"
              onClick={() => workspaceActive && setSelection(null)}
              sx={{
                bgcolor: "#eef2ff",
                color: EDITOR_ACCENT,
                fontWeight: 600,
                maxWidth: 280,
                cursor: workspaceActive ? "pointer" : "default",
                "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
                "&:hover": workspaceActive
                  ? {
                      bgcolor: "#e0e7ff",
                    }
                  : undefined,
              }}
            />
            {dirty ? (
              <Chip
                label="Modifiche non salvate"
                size="small"
                sx={{
                  bgcolor: "#fff1f2",
                  color: "#b30047",
                  fontWeight: 600,
                }}
              />
            ) : null}
          </Stack>
        </Toolbar>
      </AppBar>

      <EditorDialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textAlign: "center", color: EDITOR_ACCENT, fontWeight: 800 }}>Email salvate</DialogTitle>
        <DialogContent dividers>
          <List disablePadding>
            {emailList.length === 0 ? (
              <ListItem>
                <ListItemText primary="Nessuna email salvata" />
              </ListItem>
            ) : (
              emailList.map((item) => (
                <ListItem
                  key={item.objectKey}
                  secondaryAction={
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Button size="small" onClick={() => handleOpenDocument(item.objectKey)} disabled={loading}>
                        Apri
                      </Button>
                      <IconButton
                        size="small"
                        aria-label="Elimina email"
                        disabled={loading}
                        onClick={() =>
                          setDeleteConfirm({
                            objectKey: item.objectKey,
                            name: formatStorageDisplayName(item, "email"),
                          })
                        }
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  }
                  sx={{ pr: 14 }}
                >
                  <ListItemText
                    primary={formatStorageDisplayName(item, "email")}
                    secondary={item.lastModified || undefined}
                  />
                </ListItem>
              ))
            )}
          </List>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
          <Button onClick={() => setOpenDialog(false)} sx={{ borderRadius: 0 }}>
            Chiudi
          </Button>
        </DialogActions>
      </EditorDialog>

      <ConfirmDialog
        open={Boolean(deleteConfirm)}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => handleDeleteEmail(deleteConfirm.objectKey)}
        title="Elimina email"
        message={`Eliminare definitivamente "${deleteConfirm?.name || "questa email"}"?`}
        confirmColor="#b30047"
      />

      <EditorDialog open={saveAsOpen} onClose={() => setSaveAsOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ textAlign: "center", color: EDITOR_ACCENT, fontWeight: 800 }}>Salva con nome</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Nome newsletter"
            value={saveAsName}
            onChange={(e) => setSaveAsName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button onClick={() => setSaveAsOpen(false)} sx={{ borderRadius: 0 }}>
            Annulla
          </Button>
          <Button
            variant="contained"
            sx={{ borderRadius: 0, bgcolor: EDITOR_ACCENT }}
            onClick={async () => {
              await handleSave(null, saveAsName);
              setSaveAsOpen(false);
            }}
          >
            Salva
          </Button>
        </DialogActions>
      </EditorDialog>

      <ConfirmDialog
        open={confirmNewOpen}
        onClose={() => setConfirmNewOpen(false)}
        onConfirm={() => {
          handleCreateNew();
          setConfirmNewOpen(false);
        }}
        title="Nuova newsletter"
        message="Ci sono modifiche non salvate. Continuare?"
        confirmColor="#b30047"
      />
    </>
  );
};

EditorToolbar.propTypes = {
  compileErrors: PropTypes.arrayOf(PropTypes.string),
  openListSignal: PropTypes.number,
};

export default EditorToolbar;
 